function createLocalAnimQueue() {
  return {
    fns: [],
    add(fn) {
      this.fns.push(fn);
    },
    tick(dt) {
      this.fns = this.fns.filter((fn) => fn(dt));
    },
    get active() {
      return this.fns.length > 0;
    },
  };
}

function resolveRegion(area, region = {}) {
  const x = area.x + area.w * (region.xk ?? 0);
  const y = area.y + area.h * (region.yk ?? 0);
  const w = area.w * (region.wk ?? 1);
  const h = area.h * (region.hk ?? 1);
  return { x, y, w, h };
}

export async function prepareRegionComposeNode(env, spec, registry) {
  const area = env.boardArea();
  const children = Array.isArray(spec.children) ? spec.children : [];

  const preparedChildren = [];
  for (const child of children) {
    const factory = registry.get(child.kind);
    if (!factory) continue;

    const regionArea = resolveRegion(area, child.region);
    const childEnv = {
      ...env,
      boardArea: () => regionArea,
    };
    const packet = await factory.prepare(childEnv, child.payload);
    preparedChildren.push({
      kind: child.kind,
      startSec: child.startSec || 0,
      payload: child.payload,
      regionArea,
      factory,
      packet,
    });
  }

  return {
    children: preparedChildren,
  };
}

export function commitRegionComposeNode(packet, ctxs, anim) {
  const localAnim = createLocalAnimQueue();
  const started = new Array(packet.children.length).fill(false);
  const rect = ctxs.ink.canvas?.getBoundingClientRect?.();
  const dpr = rect && rect.width > 0 ? Math.max(1, ctxs.ink.canvas.width / rect.width) : 1;

  const childSurfaces = packet.children.map(() => {
    const surface = document.createElement('canvas');
    surface.width = ctxs.ink.canvas.width;
    surface.height = ctxs.ink.canvas.height;
    const ctx = surface.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { canvas: surface, ctx };
  });

  let t = 0;

  anim.add((dt) => {
    t += dt;

    for (let i = 0; i < packet.children.length; i++) {
      const child = packet.children[i];
      const childSurface = childSurfaces[i];
      if (!childSurface.ctx) continue;
      if (!started[i] && t >= child.startSec) {
        child.factory.commit(
          child.packet,
          { ink: childSurface.ctx, interaction: ctxs.interaction },
          localAnim,
          child.payload
        );
        started[i] = true;
      }
    }

    localAnim.tick(dt);

    for (let i = 0; i < packet.children.length; i++) {
      if (!started[i]) continue;
      const child = packet.children[i];
      const childSurface = childSurfaces[i];
      if (!childSurface.ctx) continue;
      const region = child.regionArea;

      ctxs.ink.save();
      ctxs.ink.beginPath();
      ctxs.ink.rect(region.x, region.y, region.w, region.h);
      ctxs.ink.clip();
      ctxs.ink.clearRect(region.x, region.y, region.w, region.h);
      ctxs.ink.drawImage(
        childSurface.canvas,
        0,
        0,
        ctxs.ink.canvas.width / dpr,
        ctxs.ink.canvas.height / dpr
      );
      ctxs.ink.restore();
    }

    const hasPendingChildren = started.some((value) => !value);
    return hasPendingChildren || localAnim.active;
  });
}
